import { useState, useEffect } from 'react'
import { reloadWithUpdateSplash } from '@/lib/updateReloadSplash'
import type { VersionCheckDialogInfo } from '@/hooks/useVersionCheck'

/** `stale-chunk`: lazy import failed after deploy (ErrorBoundary), not Firestore version drift. */
export type VersionCheckDialogVariant = 'default' | 'stale-chunk'

interface VersionCheckDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  variant?: VersionCheckDialogVariant
  /** From {@link useVersionCheck} snapshot — avoids a second Firestore read. */
  versionInfo?: VersionCheckDialogInfo | null
  versionInfoLoading?: boolean
}

export function VersionCheckDialog({
  isOpen,
  onOpenChange,
  variant = 'default',
  versionInfo = null,
  versionInfoLoading = false,
}: VersionCheckDialogProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || !isOpen) return null

  const handleReload = () => {
    console.log('[VersionCheck] User clicked Reload, reloading page...')
    onOpenChange(false)
    reloadWithUpdateSplash()
  }

  if (variant === 'stale-chunk') {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0" />
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
          <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto relative">
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-semibold text-foreground text-center">
                Update Available 🤩
              </h2>
              <p className="text-base text-foreground text-center leading-relaxed">
                This application has been updated. Please refresh to see the latest content.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="flex-1 bg-muted hover:bg-muted/80 text-foreground font-semibold py-3 rounded-lg transition-colors"
                >
                  Later
                </button>
                <button
                  type="button"
                  onClick={handleReload}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Reload
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const notificationType = versionInfo?.notificationType ?? 'none'
  const isDismissable = versionInfo?.isDismissable ?? true
  const isHardNative = versionInfo?.isHardNative ?? false
  const platform = versionInfo?.platform ?? 'desktop-web'
  const loading = versionInfoLoading || !versionInfo

  const handleClose = () => {
    if (isDismissable) {
      onOpenChange(false)
    }
  }

  const handleLater = () => {
    const now = Date.now()
    localStorage.setItem('versionCheck_laterClicked', now.toString())
    console.log('[VersionCheck] User clicked Later, will not show again for 24 hours')
    onOpenChange(false)
  }

  const handleUpdate = () => {
    const needsWebReload =
      notificationType === 'web' ||
      (notificationType === 'both' && !isHardNative)

    if (needsWebReload) {
      handleReload()
      return
    }

    if (platform === 'ios-native' || platform === 'ios-web') {
      window.open('https://apps.apple.com/fi/app/getmoretats/id6754553245', '_blank', 'noopener noreferrer')
    } else if (platform === 'android-native' || platform === 'android-web') {
      window.open('https://play.google.com/store/apps/details?id=com.bareminimum.getmoretats', '_blank', 'noopener noreferrer')
    } else {
      handleReload()
    }
  }

  const getDialogContent = () => {
    const isWebOnly = notificationType === 'web'
    const isNativeOnly = notificationType === 'ios' || notificationType === 'android'
    const isBoth = notificationType === 'both'

    if (isWebOnly && isDismissable) {
      return {
        description: 'We have some new features waiting for you. Reload the app to check them out!',
        buttonText: 'Reload',
        buttonAction: handleReload,
      }
    }

    if (isWebOnly && !isDismissable) {
      return {
        description:
          'Sorry for the interruption! A reload of the app is required so that everything runs smoothly. Thank you!',
        buttonText: 'Reload',
        buttonAction: handleReload,
      }
    }

    if (isNativeOnly && isDismissable) {
      return {
        description: 'We have some new features waiting for you. Update the app to check them out!',
        buttonText: 'Update',
        buttonAction: handleUpdate,
      }
    }

    if (isNativeOnly && !isDismissable) {
      return {
        description:
          'Sorry for the interruption! A new update is required to make sure the app runs smoothly. Thank you!',
        buttonText: 'Update',
        buttonAction: handleUpdate,
      }
    }

    if (isBoth && !isDismissable) {
      if (isHardNative) {
        return {
          description:
            'Sorry for the interruption! A new update is required to make sure the app runs smoothly. Thank you!',
          buttonText: 'Update',
          buttonAction: handleUpdate,
        }
      }
      return {
        description:
          'Sorry for the interruption! A reload of the app is required so that everything runs smoothly. Thank you!',
        buttonText: 'Reload',
        buttonAction: handleReload,
      }
    }

    return {
      description: 'We have some new features waiting for you. Reload the app to check them out!',
      buttonText: 'Reload',
      buttonAction: handleReload,
    }
  }

  const dialogContent = getDialogContent()

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
        onClick={handleClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
        <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto relative">
          <div className="p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground text-center">
              {!isDismissable ? 'Update Required 😯' : 'Update Available 🤩'}
            </h2>

            {loading ? (
              <div className="text-center py-4 text-foreground">
                Loading version information...
              </div>
            ) : (
              <p className="text-base text-foreground text-center leading-relaxed">
                {dialogContent.description}
              </p>
            )}

            {!loading && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  {isDismissable && (
                    <button
                      type="button"
                      onClick={handleLater}
                      className="flex-1 bg-muted hover:bg-muted/80 text-foreground font-semibold py-3 rounded-lg transition-colors"
                    >
                      Later
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={dialogContent.buttonAction}
                    className={`${isDismissable ? 'flex-1' : 'w-full'} bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors`}
                  >
                    {dialogContent.buttonText}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
